import React, { useRef, useEffect } from 'react';
import { Settings, ChevronDown, Edit2, Layers, FileText, History as HistoryIcon, RotateCcw } from 'lucide-react';

interface SubHeaderToolsDropdownProps {
  showToolsDropdown: boolean;
  setShowToolsDropdown: (show: boolean) => void;
  toolsDropdownRef: React.RefObject<HTMLDivElement | null>;
  setViewMode: (mode: any) => void;
  onPrefetch?: (view: string) => void;
  onShowBiography?: () => void;
  onShowTimeMachine?: () => void;
  onCloseOthers?: () => void;
}

export const SubHeaderToolsDropdown: React.FC<SubHeaderToolsDropdownProps> = ({
  showToolsDropdown,
  setShowToolsDropdown,
  toolsDropdownRef,
  setViewMode,
  onPrefetch,
  onShowBiography,
  onShowTimeMachine,
  onCloseOthers
}) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(event.target as Node)) {
        setShowToolsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [toolsDropdownRef, setShowToolsDropdown]);

  return (
    <div className="relative" ref={toolsDropdownRef}>
      <button 
        type="button"
        onClick={() => {
          setShowToolsDropdown(!showToolsDropdown);
          onCloseOthers?.();
        }}
        className={`flex items-center justify-center gap-1 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all h-10 border px-2.5 ${
          showToolsDropdown 
            ? 'bg-slate-800 text-white shadow-inner border-slate-800' 
            : 'bg-slate-100 text-slate-500 hover:text-indigo-600 hover:bg-white dark:bg-slate-800 dark:border-slate-800 shadow-sm'
        }`}
      >
        <Settings size={18} className={showToolsDropdown ? 'text-white' : ''} />
        <ChevronDown size={14} className={`transition-transform duration-200 ${showToolsDropdown ? 'rotate-180' : ''}`} />
      </button>

      {showToolsDropdown && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 p-2 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-50">
          <div className="px-2 py-1 mb-1 border-b border-slate-100 dark:border-slate-800">
             <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Werkzeuge</p>
          </div>
          
          <button 
            type="button"
            onClick={() => { setViewMode('conceptBuilder'); setShowToolsDropdown(false); }} 
            onMouseEnter={() => onPrefetch?.('conceptBuilder')}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-indigo-50 dark:hover:bg-slate-800 rounded-xl transition-all group"
            title="Strategisches Framework zur Konzeptentwicklung"
          >
            <div className="w-6 h-6 rounded-md bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 group-hover:scale-110 transition-transform shrink-0">
              <Edit2 size={14} />
            </div>
            <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">Konzept-Builder</span>
          </button>

          <button 
            type="button"
            onClick={() => { setViewMode('concept'); setShowToolsDropdown(false); }} 
            onMouseEnter={() => onPrefetch?.('concept')}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-blue-50 dark:hover:bg-slate-800 rounded-xl transition-all group"
            title="Ausformuliertes Gesamtkonzept ansehen"
          >
            <div className="w-6 h-6 rounded-md bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform shrink-0">
              <Layers size={14} />
            </div>
            <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">Projekt-Konzept</span>
          </button>
          
          <button 
            type="button"
            onClick={() => { setViewMode('report'); setShowToolsDropdown(false); }} 
            onMouseEnter={() => onPrefetch?.('report')}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-emerald-50 dark:hover:bg-slate-800 rounded-xl transition-all group"
            title="Konzept als PDF-Bericht generieren"
          >
            <div className="w-6 h-6 rounded-md bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform shrink-0">
              <FileText size={14} />
            </div>
            <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">Projekt-Bericht</span>
          </button>

          <div className="h-px bg-slate-100 dark:bg-slate-800 my-1 mx-1" />

          <button 
            type="button"
            onClick={() => { 
              onShowBiography?.();
              setShowToolsDropdown(false); 
            }} 
            className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-amber-50 dark:hover:bg-slate-800 rounded-xl transition-all group"
            title="Projekt-Chronik und Besprechungsverlauf"
          >
            <div className="w-6 h-6 rounded-md bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 group-hover:scale-110 transition-transform shrink-0">
              <HistoryIcon size={14} />
            </div>
            <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">Projekt-Biografie</span>
          </button>

          <button 
            type="button"
            onClick={() => { 
              onShowTimeMachine?.();
              setShowToolsDropdown(false); 
            }} 
            className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-indigo-50 dark:hover:bg-slate-800 rounded-xl transition-all group mt-0.5"
            title="Vorherige Projektstände wiederherstellen"
          >
            <div className="w-6 h-6 rounded-md bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 group-hover:scale-110 transition-transform shrink-0">
              <RotateCcw size={14} />
            </div>
            <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">Projekt-Zeitreise</span>
          </button>
        </div>
      )}
    </div>
  );
};
